import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/infrastructure/api/client";
import {
  ApiError,
  type ScheduleResponseDto,
  type CreateScheduleDto,
  type UpdateScheduleDto,
  type SubmitScheduleResponseDto,
} from "@tabipla/shared";

// Query keys
export const scheduleKeys = {
  all: ["schedules"] as const,
  lists: () => [...scheduleKeys.all, "list"] as const,
  list: (tripId: string) => [...scheduleKeys.lists(), tripId] as const,
  detail: (tripId: string, scheduleId: string) =>
    [...scheduleKeys.all, "detail", tripId, scheduleId] as const,
};

// Get all schedules for a trip
export function useSchedules(tripId: string) {
  return useQuery({
    queryKey: scheduleKeys.list(tripId),
    queryFn: async () => {
      const response = await apiClient.get<ScheduleResponseDto[]>(`/api/trips/${tripId}/schedules`);
      if (!response.success) {
        throw ApiError.fromResponse(response.error, "Failed to fetch schedules");
      }
      return response.data;
    },
    enabled: !!tripId,
  });
}

// Get a specific schedule
export function useSchedule(tripId: string, scheduleId: string) {
  return useQuery({
    queryKey: scheduleKeys.detail(tripId, scheduleId),
    queryFn: async () => {
      const response = await apiClient.get<ScheduleResponseDto>(
        `/api/trips/${tripId}/schedules/${scheduleId}`
      );
      if (!response.success) {
        throw ApiError.fromResponse(response.error, "Failed to fetch schedule");
      }
      return response.data;
    },
    enabled: !!tripId && !!scheduleId,
  });
}

// Create a new schedule
export function useCreateSchedule(tripId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateScheduleDto) => {
      const response = await apiClient.post<ScheduleResponseDto>(
        `/api/trips/${tripId}/schedules`,
        data
      );
      if (!response.success) {
        throw ApiError.fromResponse(response.error, "Failed to create schedule");
      }
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: scheduleKeys.list(tripId) });
    },
  });
}

// Update a schedule
export function useUpdateSchedule(tripId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ scheduleId, data }: { scheduleId: string; data: UpdateScheduleDto }) => {
      const response = await apiClient.put<ScheduleResponseDto>(
        `/api/trips/${tripId}/schedules/${scheduleId}`,
        data
      );
      if (!response.success) {
        throw ApiError.fromResponse(response.error, "Failed to update schedule");
      }
      return response.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: scheduleKeys.list(tripId) });
      queryClient.invalidateQueries({
        queryKey: scheduleKeys.detail(tripId, variables.scheduleId),
      });
    },
  });
}

// Close a schedule
export function useCloseSchedule(tripId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (scheduleId: string) => {
      const response = await apiClient.post<ScheduleResponseDto>(
        `/api/trips/${tripId}/schedules/${scheduleId}/close`
      );
      if (!response.success) {
        throw ApiError.fromResponse(response.error, "Failed to close schedule");
      }
      return response.data;
    },
    onSuccess: (_, scheduleId) => {
      queryClient.invalidateQueries({ queryKey: scheduleKeys.list(tripId) });
      queryClient.invalidateQueries({ queryKey: scheduleKeys.detail(tripId, scheduleId) });
    },
  });
}

// Delete a schedule
export function useDeleteSchedule(tripId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (scheduleId: string) => {
      const response = await apiClient.delete(`/api/trips/${tripId}/schedules/${scheduleId}`);
      if (!response.success) {
        throw ApiError.fromResponse(response.error, "Failed to delete schedule");
      }
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: scheduleKeys.list(tripId) });
    },
  });
}

// Submit response to a schedule
export function useSubmitScheduleResponse(tripId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      scheduleId,
      data,
    }: {
      scheduleId: string;
      data: SubmitScheduleResponseDto;
    }) => {
      const response = await apiClient.post<ScheduleResponseDto>(
        `/api/trips/${tripId}/schedules/${scheduleId}/respond`,
        data
      );
      if (!response.success) {
        throw ApiError.fromResponse(response.error, "Failed to submit response");
      }
      return response.data;
    },
    onMutate: async ({ scheduleId, data }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: scheduleKeys.list(tripId) });

      // Snapshot the previous value
      const previousSchedules = queryClient.getQueryData<ScheduleResponseDto[]>(
        scheduleKeys.list(tripId)
      );

      // Optimistically update to the new value
      if (previousSchedules) {
        queryClient.setQueryData<ScheduleResponseDto[]>(
          scheduleKeys.list(tripId),
          previousSchedules.map((schedule) => {
            if (schedule.id !== scheduleId) return schedule;

            return {
              ...schedule,
              myResponses: data.responses,
            };
          })
        );
      }

      return { previousSchedules };
    },
    onError: (_, __, context) => {
      // Rollback on error
      if (context?.previousSchedules) {
        queryClient.setQueryData(scheduleKeys.list(tripId), context.previousSchedules);
      }
    },
    onSettled: (_, __, variables) => {
      queryClient.invalidateQueries({ queryKey: scheduleKeys.list(tripId) });
      queryClient.invalidateQueries({
        queryKey: scheduleKeys.detail(tripId, variables.scheduleId),
      });
    },
  });
}
